import { useState, useRef } from 'react'
import { Drawer, Modal } from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'

import Kanban from '@/components/ui/kanban/Kanban'

import {
    serviceCaseStatuses,
    typeFilterOptions,
    handlingLocationFilterOptions,
} from '@/lib/serviceCaseStatuses'

import ServiceCaseKanbanItem from './ServiceCaseKanbanItem'
import ShowServiceCase from './ShowServiceCase'

export default function ServiceCasesKanban() {
    const [openedServiceCaseId, setOpenedServiceCaseId] = useState(null)
    const [drawerOpened, setDrawerOpened] = useState(false)
    const refetchRef = useRef(null)
    const { width: viewportWidth } = useViewportSize()
    const ModalComponent = viewportWidth > 128 ? Modal : Drawer

    const handleOpen = (id) => {
        setOpenedServiceCaseId(id)
        setDrawerOpened(true)
    }

    const handleClose = () => {
        setDrawerOpened(false)
    }
    const availableFilters = [
        {
            name: 'Type',
            key: 'type',
            type: 'multiselect',
            options: typeFilterOptions,
        },
        {
            name: 'Lieu de traitement',
            key: 'is_handled_by',
            type: 'multiselect',
            options: handlingLocationFilterOptions,
        },
        {
            name: 'Archivé',
            key: 'is_archived',
            type: 'select',
            options: [
                { value: '0', label: 'Non archivés' },
                { value: '1', label: 'Archivés' },
            ],
        },
    ]

    const getColumnProps = (slug) => {
        const status = serviceCaseStatuses[slug] || {
            label: slug,
            classes: { bg: 'bg-gray-50', text: 'text-gray-700' },
        }

        return {
            name: status.label,
            bgColor: status.classes.bg,
            textColor: status.classes.text,
        }
    }

    // Available columns - statuts affichés par défaut dans le kanban
    const availableColumns = [
        {
            name: serviceCaseStatuses.waiting_acceptation.label,
            key: 'waiting_acceptation',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.customer_contacted.label,
            key: 'customer_contacted',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.in_store_dropoff.label,
            key: 'in_store_dropoff',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.waiting_shipment.label,
            key: 'waiting_shipment',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.received.label,
            key: 'received',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.watchmaker_diagnosis.label,
            key: 'watchmaker_diagnosis',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.quote_to_prepare.label,
            key: 'quote_to_prepare',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.quote_sent.label,
            key: 'quote_sent',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.quote_accepted.label,
            key: 'quote_accepted',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.quote_refused.label,
            key: 'quote_refused',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.repairing.label,
            key: 'repairing',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.quality_control.label,
            key: 'quality_control',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.to_store.label,
            key: 'to_store',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.with_courier.label,
            key: 'with_courier',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.ready_in_store.label,
            key: 'ready_in_store',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.to_ship.label,
            key: 'to_ship',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.restocking.label,
            key: 'restocking',
            display: 'optional_default',
        },
        {
            name: serviceCaseStatuses.pending_swap.label,
            key: 'pending_swap',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.pending_refund.label,
            key: 'pending_refund',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.finished.label,
            key: 'finished',
            display: 'optional',
        },
        {
            name: serviceCaseStatuses.refused.label,
            key: 'refused',
            display: 'optional',
        },
    ]

    return (
        <>
            <Kanban
                availableColumns={availableColumns}
                availableFilters={availableFilters}
                engineApiEndpoint={'/service-cases/kanban'}
                getColumnProps={getColumnProps}
                ItemComponent={ServiceCaseKanbanItem}
                onItemOpen={handleOpen}
                refetchRef={refetchRef}
            />

            <ModalComponent
                opened={drawerOpened}
                onClose={handleClose}
                position="right"
                withCloseButton={false}
                size={900}
                className="relative"
                padding="0"
            >
                {openedServiceCaseId && (
                    <ShowServiceCase
                        serviceCaseId={openedServiceCaseId}
                        withScrollableScreen={false}
                        onClose={handleClose}
                        onMutate={() => refetchRef.current?.()}
                    />
                )}
            </ModalComponent>
        </>
    )
}
